import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/*
 * A horizontal strip of cards: swipe or scroll it natively (scroll-snap), or step one
 * card at a time with the arrow buttons, which dim at either end. The next card always
 * peeks in from the edge so the strip reads as one.
 */

export const Carousel = ({
  label,
  prevLabel,
  nextLabel,
  onStep,
  children,
}: {
  label: string;
  prevLabel: string;
  nextLabel: string;
  /** Called on each button press, for analytics. Swipes are not reported. */
  onStep?: (direction: 'next' | 'back') => void;
  children: ReactNode;
}) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  const step = (dir: 1 | -1) => {
    const el = trackRef.current;
    const card = el?.firstElementChild as HTMLElement | null;
    if (!el || !card) return;
    onStep?.(dir === 1 ? 'next' : 'back');
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    el.scrollBy({ left: dir * (card.offsetWidth + gap), behavior: 'smooth' });
  };

  return (
    <div className="carousel">
      <div className="carousel-nav">
        <button type="button" aria-label={prevLabel} disabled={atStart} onClick={() => step(-1)}>
          <svg className="i" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19 12H5M11 6l-6 6 6 6" />
          </svg>
        </button>
        <button type="button" aria-label={nextLabel} disabled={atEnd} onClick={() => step(1)}>
          <svg className="i" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
      <ul className="carousel-track" ref={trackRef} aria-label={label}>
        {children}
      </ul>
    </div>
  );
};
